/* eslint-disable react-hooks/exhaustive-deps */
import { FC, Fragment, Suspense, useEffect, useMemo, useState } from 'react'
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'redux/store'
import { setUser } from 'redux/ui/common/userUI'

import { Box, LinearProgress } from '@mui/material'

import { useGET_userQuery } from 'redux/api/userAPI'

import LoginPage from 'pages/public/LoginPage'
import NotFoundPage from 'pages/public/NotFoundPage'

import Layout from 'components/organisms/layout/Layout'

import { INavItem, navigationModel } from 'utils/navigationModel'

import { RoleType } from 'types'

const AppRouter = () => {
  const dispatch = useDispatch<AppDispatch>()

  const location = useLocation()
  const navigate = useNavigate()

  const userToken = window.localStorage.getItem('token')

  const storedUserData = useSelector((state: RootState) => state['user'].user)

  const { isFetching, data: fetchedUserData } = useGET_userQuery(null, { skip: !userToken || storedUserData })

  const userData = storedUserData || fetchedUserData

  // Get routes & navigation data model

  const [routeItems, setRouteItems] = useState([])
  
  useEffect(() => {
    if (userData?.role) {
      const newRouteItems = navigationModel(userData?.role)
      setRouteItems(newRouteItems)
    }
  }, [userData?.role])

  // Order permissions by navigation order
  const userRoleType = useMemo(() => userData?.role, [userData?.role])

  useEffect(() => {
    if (!userToken && location.pathname !== '/password_reset/confirm/') {
      navigate('/login')
    } else {
      if (location.pathname === '/login') {
        navigate('/')
      }
    }
  }, [userToken, navigate, location.pathname])

  useEffect(() => {
    if (fetchedUserData) {
      dispatch(setUser(fetchedUserData))
    }
  }, [fetchedUserData])


  const renderProtectedRoutesRecursive = (routeItems: INavItem[], userRoleType: RoleType) =>
    routeItems.map((routeItem, index) =>
      routeItem.children ? (
        renderProtectedRoutesRecursive(routeItem.children, userRoleType)
      ) : (
        <Fragment key={index}>
          <Route
            path={routeItem.route}
            element={
              <Suspense fallback={<LazyPageLoading />}>
                <ProtectedRoute routeItem={routeItem} roles={routeItem.roles} userRoleType={userRoleType} />
              </Suspense>
            }
          >
            {routeItem?.nestedRoutes &&
              routeItem.nestedRoutes.length &&
              routeItem.nestedRoutes.map((nestedRoute, nestedIndex) => (
                <Route key={nestedIndex} path={nestedRoute} element={routeItem?.nestedComponents?.[nestedIndex]} />
              ))}
          </Route>
          {routeItem?.subRoutes &&
            routeItem.subRoutes.length &&
            routeItem.subRoutes.map((subRoute, subIndex) => (
              <Route key={subIndex} path={subRoute} element={routeItem?.subComponents?.[subIndex]} />
            ))}
        </Fragment>
      ),
    )

  const redirectMap = {
    student: 'home',
    teacher: 'teacher-workspace',
    parent: 'parent-workspace'
  }
  const redirectRoute = userRoleType in redirectMap ? redirectMap[userRoleType] : 'dashboard'

  const renderProtectedRoutes = useMemo(
    () => renderProtectedRoutesRecursive(routeItems, userRoleType),
    [routeItems, userRoleType],
  )

  return (
    <Routes>
      <Route path='/' element={<Layout isLoading={isFetching} userToken={userToken} />}>
        {/* Protected routes */}
        <Route path='/' element={<Navigate to={redirectRoute} replace />} />
        {renderProtectedRoutes}

        {/* Public routes */}
        <Route path='login' element={<LoginPage />} />
        <Route path='password_reset/confirm/' element={<LoginPage />} />
        <Route path='*' element={<NotFoundPage />} />
      </Route>
    </Routes>
  )
}

export default AppRouter

interface IRouteProps {
  routeItem: INavItem
  roles: RoleType[]
  userRoleType: RoleType
}

const ProtectedRoute: FC<IRouteProps> = ({ routeItem, roles, userRoleType }) => {
  const hasAccess = roles.includes(userRoleType)
  const location = useLocation()

  return hasAccess ? routeItem.component : <Navigate to='/unauthorized' state={{ from: location }} replace />
}

const LazyPageLoading = () => {
  return (
    <Box sx={{ width: '110%', margin: '-1.5rem', opacity: '0.5' }}>
      <LinearProgress />
    </Box>
  )
}
